import { useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { apiClient, type ApiResponse } from '@/lib/api-client';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';

interface Role {
  id: string;
  name: string;
  description: string;
  isSystemRole: boolean;
  dataScope: 'assigned' | 'team' | 'all';
  permissions: Record<string, boolean>;
  userCount: number;
  createdAt: string;
}

interface PermissionDefinition {
  key: string;
  label: string;
  category: string;
}

const MAX_CUSTOM_ROLES = 20;

/**
 * RolesPermissions — manage roles with a permission matrix.
 * Requirements: 3.1, 3.2, 3.3, 3.9
 */
export function RolesPermissions() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [copyingRole, setCopyingRole] = useState<Role | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [membersRoleId, setMembersRoleId] = useState<string | null>(null);

  const { data: roles, isLoading: rolesLoading } = useQuery({
    queryKey: ['settings', 'roles'],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Role[]>>('/roles');
      return response.data.data;
    },
  });

  const { data: permissionDefs } = useQuery({
    queryKey: ['settings', 'permissions-definitions'],
    queryFn: async () => {
      const response =
        await apiClient.get<ApiResponse<PermissionDefinition[]>>('/roles/permissions');
      return response.data.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: Partial<Role>) => {
      const response = await apiClient.post<ApiResponse<Role>>('/roles', data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'roles'] });
      setIsEditorOpen(false);
      setEditingRole(null);
      toast({ variant: 'success', title: 'Role created successfully' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Role> }) => {
      const response = await apiClient.put<ApiResponse<Role>>(`/roles/${id}`, data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'roles'] });
      setIsEditorOpen(false);
      setEditingRole(null);
      toast({ variant: 'success', title: 'Role updated successfully' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/roles/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'roles'] });
      toast({ variant: 'success', title: 'Role deleted' });
    },
  });

  const customRoleCount = useMemo(
    () => (roles ?? []).filter((r) => !r.isSystemRole).length,
    [roles],
  );

  const canAddMore = customRoleCount < MAX_CUSTOM_ROLES;

  const membersRole = roles?.find((r) => r.id === membersRoleId) ?? null;

  const handleNewRole = () => {
    setEditingRole(null);
    setCopyingRole(null);
    setIsEditorOpen(true);
  };

  const handleEditRole = (role: Role) => {
    setEditingRole(role);
    setCopyingRole(null);
    setIsEditorOpen(true);
  };

  // Copy opens the editor pre-filled with the source role's permissions and
  // scope so the user can rename before saving it as a new custom role.
  const handleCopyRole = (role: Role) => {
    setEditingRole(null);
    setCopyingRole(role);
    setIsEditorOpen(true);
  };

  const handleSaveRole = (data: Partial<Role>) => {
    if (editingRole) {
      updateMutation.mutate({ id: editingRole.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  if (rolesLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Roles & Permissions"
          description={`Manage user roles and their permissions. Maximum ${MAX_CUSTOM_ROLES} custom roles.`}
          action={
            <Button size="sm" onClick={handleNewRole} disabled={!canAddMore}>
              New Role
            </Button>
          }
        />
        <CardContent>
          {!roles || roles.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No roles configured.</p>
          ) : (
            <div className="divide-y divide-border dark:divide-border-dark">
              {roles.map((role) => (
                <div
                  key={role.id}
                  className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {role.name}
                      </span>
                      {role.isSystemRole && (
                        <Badge variant="info" size="sm">
                          System
                        </Badge>
                      )}
                      <Badge variant="default" size="sm">
                        {role.dataScope}
                      </Badge>
                    </div>
                    {role.description && (
                      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                        {role.description}
                      </p>
                    )}
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      {role.userCount} user{role.userCount !== 1 ? 's' : ''} assigned
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setMembersRoleId(role.id)}>
                      Members
                    </Button>
                    {/* Copy is available for every role (incl. system) — it
                        creates a new custom role from this role's permissions. */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopyRole(role)}
                      disabled={!canAddMore}
                      title={
                        canAddMore
                          ? 'Copy this role to a new role'
                          : `Maximum ${MAX_CUSTOM_ROLES} custom roles reached`
                      }
                    >
                      Copy
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleEditRole(role)}>
                      {role.isSystemRole ? 'View' : 'Edit'}
                    </Button>
                    {!role.isSystemRole && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteMutation.mutate(role.id)}
                        disabled={role.userCount > 0}
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            {customRoleCount} / {MAX_CUSTOM_ROLES} custom roles used
          </p>
        </CardContent>
      </Card>

      {/* Role Editor with Permission Matrix */}
      {isEditorOpen && (
        <RoleEditorModal
          role={editingRole}
          copyFrom={copyingRole}
          permissionDefs={permissionDefs ?? []}
          onSave={handleSaveRole}
          onClose={() => {
            setIsEditorOpen(false);
            setEditingRole(null);
            setCopyingRole(null);
          }}
          isSaving={createMutation.isPending || updateMutation.isPending}
        />
      )}

      {/* Role Members Panel */}
      {membersRoleId && membersRole && (
        <RoleMembersModal role={membersRole} onClose={() => setMembersRoleId(null)} />
      )}
    </div>
  );
}

// ─── Role Editor Modal with Permission Matrix ─────────────────────────────────

interface RoleEditorModalProps {
  role: Role | null;
  /** When set (and role is null), pre-fills a NEW role from this source (copy). */
  copyFrom?: Role | null;
  permissionDefs: PermissionDefinition[];
  onSave: (data: Partial<Role>) => void;
  onClose: () => void;
  isSaving: boolean;
}

function RoleEditorModal({
  role,
  copyFrom,
  permissionDefs,
  onSave,
  onClose,
  isSaving,
}: RoleEditorModalProps) {
  const isCopying = !role && !!copyFrom;
  // Editing seeds from the role; copying seeds from copyFrom (with a suggested
  // new name and a fresh permissions copy); creating starts blank.
  const seed = role ?? copyFrom ?? null;
  const [form, setForm] = useState({
    name: role?.name ?? (copyFrom ? `${copyFrom.name} (Copy)` : ''),
    description: seed?.description ?? '',
    dataScope: seed?.dataScope ?? ('assigned' as 'assigned' | 'team' | 'all'),
    permissions: seed?.permissions ? { ...seed.permissions } : ({} as Record<string, boolean>),
  });
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  // A copy is always a brand-new custom role, so the form is fully editable
  // even when copying FROM a system role.
  const isSystemRole = role?.isSystemRole ?? false;

  // Group permissions by category
  const groupedPermissions = useMemo(() => {
    const groups: Record<string, PermissionDefinition[]> = {};
    for (const perm of permissionDefs) {
      if (!groups[perm.category]) {
        groups[perm.category] = [];
      }
      groups[perm.category].push(perm);
    }
    return groups;
  }, [permissionDefs]);

  // Apply the search filter — matches on permission label, permission key, or
  // the category name. A category with a matching name keeps all its perms.
  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groupedPermissions;
    const result: Record<string, PermissionDefinition[]> = {};
    for (const [category, perms] of Object.entries(groupedPermissions)) {
      const categoryMatches = category.toLowerCase().includes(q);
      const matched = categoryMatches
        ? perms
        : perms.filter((p) => p.label.toLowerCase().includes(q) || p.key.toLowerCase().includes(q));
      if (matched.length > 0) result[category] = matched;
    }
    return result;
  }, [groupedPermissions, search]);

  const filteredCategories = Object.keys(filteredGroups);
  const isSearching = search.trim().length > 0;

  const totalSelected = useMemo(
    () => permissionDefs.filter((p) => form.permissions[p.key]).length,
    [permissionDefs, form.permissions],
  );

  const handlePermissionToggle = (key: string) => {
    if (isSystemRole) return;
    setForm((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [key]: !prev.permissions[key],
      },
    }));
  };

  // Toggle every permission currently visible within a category (respects the
  // active search filter, so "enable all" only affects what the user sees).
  const handleCategoryToggle = (category: string, enabled: boolean) => {
    if (isSystemRole) return;
    const categoryPerms = filteredGroups[category] ?? [];
    const updated = { ...form.permissions };
    for (const perm of categoryPerms) {
      updated[perm.key] = enabled;
    }
    setForm((prev) => ({ ...prev, permissions: updated }));
  };

  const isCategoryFullyEnabled = (category: string) => {
    const categoryPerms = filteredGroups[category] ?? [];
    return categoryPerms.length > 0 && categoryPerms.every((p) => form.permissions[p.key]);
  };

  const isCategoryPartiallyEnabled = (category: string) => {
    const categoryPerms = filteredGroups[category] ?? [];
    return categoryPerms.some((p) => form.permissions[p.key]) && !isCategoryFullyEnabled(category);
  };

  const getCategoryEnabledCount = (category: string) => {
    const categoryPerms = filteredGroups[category] ?? [];
    return categoryPerms.filter((p) => form.permissions[p.key]).length;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(form);
  };

  // Select/Deselect all respects the active search filter.
  const handleSelectAll = () => {
    if (isSystemRole) return;
    const updated = { ...form.permissions };
    for (const perms of Object.values(filteredGroups)) {
      for (const perm of perms) updated[perm.key] = true;
    }
    setForm((prev) => ({ ...prev, permissions: updated }));
  };

  const handleDeselectAll = () => {
    if (isSystemRole) return;
    const updated = { ...form.permissions };
    for (const perms of Object.values(filteredGroups)) {
      for (const perm of perms) updated[perm.key] = false;
    }
    setForm((prev) => ({ ...prev, permissions: updated }));
  };

  const allExpanded = filteredCategories.every((c) => !collapsed[c]);
  const toggleExpandCollapseAll = () => {
    if (allExpanded) {
      const next: Record<string, boolean> = {};
      for (const c of filteredCategories) next[c] = true;
      setCollapsed(next);
    } else {
      setCollapsed({});
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={
        role
          ? `${isSystemRole ? 'View' : 'Edit'} Role: ${role.name}`
          : isCopying
            ? `Copy Role: ${copyFrom?.name}`
            : 'New Role'
      }
      size="xl"
    >
      <form onSubmit={handleSubmit} className="flex max-h-[78vh] flex-col">
        <div className="space-y-5 overflow-y-auto pr-1">
          {/* Role Details */}
          <div className="grid grid-cols-1 gap-4">
            <Input
              label="Role Name"
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              required
              disabled={isSystemRole}
              placeholder="e.g., Senior Accountant"
            />
          </div>
          <Input
            label="Description"
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
            disabled={isSystemRole}
            placeholder="Brief description of this role's purpose"
          />

          {/* Permission Picker */}
          <div>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Permissions
                </h4>
                <Badge variant={totalSelected > 0 ? 'info' : 'default'} size="sm">
                  {totalSelected} selected
                </Badge>
              </div>
              <button
                type="button"
                onClick={toggleExpandCollapseAll}
                className="text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {allExpanded ? 'Collapse all' : 'Expand all'}
              </button>
            </div>

            {/* Search + bulk actions */}
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <div className="relative min-w-[200px] flex-1">
                <svg
                  className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.8}
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m21 21-4.3-4.3m1.8-5.2a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z"
                  />
                </svg>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search permissions…"
                  aria-label="Search permissions"
                  className="w-full rounded-md border border-border bg-white py-1.5 pl-8 pr-8 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark dark:text-gray-100"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    aria-label="Clear search"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              {!isSystemRole && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="rounded-md border border-brand-200 px-2 py-1 text-xs font-medium text-brand-700 hover:bg-brand-50 dark:border-brand-800 dark:text-brand-300 dark:hover:bg-brand-900/20"
                  >
                    {isSearching ? 'Select filtered' : 'Select all'}
                  </button>
                  <button
                    type="button"
                    onClick={handleDeselectAll}
                    className="rounded-md border border-border px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-border-dark dark:text-gray-300 dark:hover:bg-gray-800"
                  >
                    {isSearching ? 'Clear filtered' : 'Clear all'}
                  </button>
                </div>
              )}
            </div>

            {/* Category accordions */}
            {filteredCategories.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border py-8 text-center text-sm text-gray-500 dark:border-border-dark dark:text-gray-400">
                No permissions match “{search}”.
              </div>
            ) : (
              <div className="space-y-2">
                {filteredCategories.map((category) => {
                  const perms = filteredGroups[category];
                  const allEnabled = isCategoryFullyEnabled(category);
                  const partial = isCategoryPartiallyEnabled(category);
                  const enabledCount = getCategoryEnabledCount(category);
                  // Auto-expand while searching; otherwise honour the toggle.
                  const isOpen = isSearching || !collapsed[category];

                  return (
                    <div
                      key={category}
                      className={`overflow-hidden rounded-lg border transition-colors ${
                        allEnabled
                          ? 'border-brand-200 dark:border-brand-800'
                          : partial
                            ? 'border-yellow-200 dark:border-yellow-800'
                            : 'border-border dark:border-border-dark'
                      }`}
                    >
                      {/* Category header row */}
                      <div
                        className={`flex items-center justify-between gap-2 px-3 py-2 ${
                          allEnabled
                            ? 'bg-brand-50/60 dark:bg-brand-900/10'
                            : partial
                              ? 'bg-yellow-50/40 dark:bg-yellow-900/10'
                              : 'bg-gray-50/60 dark:bg-gray-800/30'
                        }`}
                      >
                        <label className="flex flex-1 cursor-pointer select-none items-center gap-2">
                          <input
                            type="checkbox"
                            checked={allEnabled}
                            ref={(el) => {
                              if (el) el.indeterminate = partial;
                            }}
                            onChange={(e) => handleCategoryToggle(category, e.target.checked)}
                            disabled={isSystemRole}
                            className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                          />
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {category}
                          </span>
                          <span
                            className={`rounded-full px-1.5 py-0.5 text-[11px] font-medium ${
                              enabledCount > 0
                                ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
                                : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                            }`}
                          >
                            {enabledCount}/{perms.length}
                          </span>
                        </label>
                        {!isSearching && (
                          <button
                            type="button"
                            onClick={() =>
                              setCollapsed((prev) => ({ ...prev, [category]: !prev[category] }))
                            }
                            aria-label={isOpen ? `Collapse ${category}` : `Expand ${category}`}
                            aria-expanded={isOpen}
                            className="rounded p-1 text-gray-400 hover:bg-gray-200/60 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                          >
                            <svg
                              className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={2}
                              stroke="currentColor"
                              aria-hidden="true"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="m19.5 8.25-7.5 7.5-7.5-7.5"
                              />
                            </svg>
                          </button>
                        )}
                      </div>

                      {/* Individual permissions */}
                      {isOpen && (
                        <div className="grid grid-cols-1 gap-x-4 gap-y-1 px-3 py-2 sm:grid-cols-2">
                          {perms.map((perm) => (
                            <label
                              key={perm.key}
                              className="flex cursor-pointer select-none items-center gap-2 rounded px-1 py-0.5 hover:bg-gray-50 dark:hover:bg-gray-800/40"
                            >
                              <input
                                type="checkbox"
                                checked={form.permissions[perm.key] ?? false}
                                onChange={() => handlePermissionToggle(perm.key)}
                                disabled={isSystemRole}
                                className="h-3.5 w-3.5 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                              />
                              <span className="text-xs text-gray-600 dark:text-gray-400">
                                {perm.label}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        {!isSystemRole && (
          <div className="mt-2 flex items-center justify-between gap-3 border-t border-border pt-3 dark:border-border-dark">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {totalSelected} permission{totalSelected !== 1 ? 's' : ''} selected
            </span>
            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" loading={isSaving} disabled={!form.name}>
                {role ? 'Update Role' : isCopying ? 'Create Copy' : 'Create Role'}
              </Button>
            </div>
          </div>
        )}

        {isSystemRole && (
          <div className="mt-2 flex justify-end border-t border-border pt-3 dark:border-border-dark">
            <Button type="button" variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        )}
      </form>
    </Modal>
  );
}

// ─── Role Members Modal ───────────────────────────────────────────────────────

interface RoleMember {
  id: string;
  name: string;
  email: string;
  status: string;
  jobRole: string;
}

interface RoleMembersModalProps {
  role: Role;
  onClose: () => void;
}

function RoleMembersModal({ role, onClose }: RoleMembersModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { can } = usePermissions();
  const [selectedUserId, setSelectedUserId] = useState('');

  const canManageRoles = can(PERMISSIONS.USER_ADMIN_MANAGE_ROLES);

  // Fetch current members of this role
  const { data: members, isLoading: membersLoading } = useQuery({
    queryKey: ['settings', 'role-members', role.id],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<RoleMember[]>>(`/roles/${role.id}/users`);
      return response.data.data;
    },
  });

  // Fetch all active users for the "Add User" picker
  const { data: allUsers } = useQuery({
    queryKey: ['settings', 'active-users'],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<RoleMember[]>>(
        '/users?pageSize=200&status=active',
      );
      return response.data.data;
    },
  });

  // Users available to add (not already in this role)
  const availableUsers = useMemo(() => {
    if (!allUsers || !members) return [];
    const memberIds = new Set(members.map((m) => m.id));
    return allUsers.filter((u) => !memberIds.has(u.id));
  }, [allUsers, members]);

  // Add user mutation
  const addMutation = useMutation({
    mutationFn: async (userId: string) => {
      await apiClient.post(`/roles/${role.id}/users`, { userId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'role-members', role.id] });
      queryClient.invalidateQueries({ queryKey: ['settings', 'roles'] });
      setSelectedUserId('');
      toast({ variant: 'success', title: 'User added to role' });
    },
  });

  // Remove user mutation
  const removeMutation = useMutation({
    mutationFn: async (userId: string) => {
      await apiClient.delete(`/roles/${role.id}/users/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'role-members', role.id] });
      queryClient.invalidateQueries({ queryKey: ['settings', 'roles'] });
      toast({ variant: 'success', title: 'User removed from role' });
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Failed to remove user from role';
      toast({ variant: 'error', title: message });
    },
  });

  const handleAdd = () => {
    if (!selectedUserId) return;
    addMutation.mutate(selectedUserId);
  };

  // Only the Administrator role must retain at least one member (otherwise the
  // tenant could be locked out). Every other role — system or custom — may have
  // its last member removed. Mirrors the backend guard in roles.service.ts.
  const isLastAdministrator = role.name === 'Administrator' && (members?.length ?? 0) <= 1;

  return (
    <Modal open onClose={onClose} title={`Members: ${role.name}`} size="lg">
      <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
        {/* Members list */}
        {membersLoading ? (
          <div className="flex items-center justify-center py-6">
            <div className="h-6 w-6 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
          </div>
        ) : !members || members.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No users assigned to this role.
          </p>
        ) : (
          <div className="divide-y divide-border dark:divide-border-dark">
            {members.map((member) => (
              <div
                key={member.id}
                className="flex items-center justify-between py-2 first:pt-0 last:pb-0"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {member.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{member.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={member.status === 'active' ? 'success' : 'default'} size="sm">
                    {member.status}
                  </Badge>
                  {canManageRoles && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeMutation.mutate(member.id)}
                      disabled={isLastAdministrator || removeMutation.isPending}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add User section */}
        {canManageRoles && (
          <div className="border-t border-border dark:border-border-dark pt-4">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Add User
            </h4>
            <div className="flex items-center gap-2">
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="flex-1 rounded-md border border-border bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark dark:text-gray-100"
              >
                <option value="">Select a user...</option>
                {availableUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </option>
                ))}
              </select>
              <Button
                size="sm"
                onClick={handleAdd}
                disabled={!selectedUserId || addMutation.isPending}
                loading={addMutation.isPending}
              >
                Add
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end pt-4">
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>
    </Modal>
  );
}
